// ---------------------------------------------------------------------------
// Assessment Agent — what the UI reads from the server, and how it asks
// (NDI-239 v2)
//
// The shapes mirror entrypoints/sdr/src/server/v21/assessment/types.ts (the
// assessment record, its tagged inputs and hand-over) and the setup API of
// the v2 spec (§5: SetupView = the stored setup without userId, plus
// `steps`). They are kept loose on purpose — every field the server may omit
// is optional — and copied rather than imported: the UI package does not
// depend on the server's sources.
//
// One request helper, plain fetch with the cookie, the app's `{ok, error}`
// envelope: a refusal throws an ApiError carrying the server's message (often
// German, from the assessment module), its status and its body.
//
// Every endpoint the assessment UI calls is in the API table below and
// nowhere else: the server's tests read the table and check each entry
// against its routers (entrypoints/sdr/tests/assessment-ui-contract.test.ts),
// so a path or a method that drifts fails a test, not a click.
// ---------------------------------------------------------------------------

/** One endpoint: its method and its path, with `:id` for the record. */
export interface Endpoint {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
}

/** Every endpoint the assessment UI calls — one per line, `{ method, path }` (the contract guard reads this). */
export const API = {
  listSetups: { method: 'GET', path: '/api/v21/assessment-setups' },
  createSetup: { method: 'POST', path: '/api/v21/assessment-setups' },
  getSetup: { method: 'GET', path: '/api/v21/assessment-setups/:id' },
  putCalculator: { method: 'PUT', path: '/api/v21/assessment-setups/:id/calculator' },
  refreshCalculator: { method: 'POST', path: '/api/v21/assessment-setups/:id/calculator/refresh' },
  putFields: { method: 'PUT', path: '/api/v21/assessment-setups/:id/fields' },
  convert: { method: 'POST', path: '/api/v21/assessment-setups/:id/convert' },
  putTemplate: { method: 'PUT', path: '/api/v21/assessment-setups/:id/template' },
  refreshTemplate: { method: 'POST', path: '/api/v21/assessment-setups/:id/template/refresh' },
  putMapping: { method: 'PUT', path: '/api/v21/assessment-setups/:id/mapping' },
  putHook: { method: 'PUT', path: '/api/v21/assessment-setups/:id/hook' },
  createExample: { method: 'POST', path: '/api/v21/assessment-setups/:id/example' },
  examplePdf: { method: 'GET', path: '/api/v21/assessment-setups/:id/example.pdf' },
  approve: { method: 'POST', path: '/api/v21/assessment-setups/:id/approve' },
  feedback: { method: 'POST', path: '/api/v21/assessment-setups/:id/feedback' },
  listAssessments: { method: 'GET', path: '/api/v21/assessments' },
  startAssessment: { method: 'POST', path: '/api/v21/assessments' },
  getAssessment: { method: 'GET', path: '/api/v21/assessments/:id' },
  assessmentPdf: { method: 'GET', path: '/api/v21/assessments/:id/pdf' },
} as const satisfies Record<string, Endpoint>;

/**
 * What each endpoint does to an approved setup (R33): `revokes` — the server sets the approval back (any change of
 * calculator, inputs, script, template, mapping, hook or example does), so the page asks first; `keeps` — it does
 * not; `read` — a GET. Typed over the whole table: a new endpoint does not compile until it is placed here.
 */
export const APPROVAL_EFFECT: Record<keyof typeof API, 'read' | 'revokes' | 'keeps'> = {
  listSetups: 'read',
  createSetup: 'keeps',
  getSetup: 'read',
  putCalculator: 'revokes',
  refreshCalculator: 'revokes',
  putFields: 'revokes',
  convert: 'revokes',
  putTemplate: 'revokes',
  refreshTemplate: 'revokes',
  putMapping: 'revokes',
  putHook: 'revokes',
  createExample: 'revokes',
  examplePdf: 'read',
  approve: 'keeps',
  feedback: 'revokes',
  listAssessments: 'read',
  startAssessment: 'keeps',
  getAssessment: 'read',
  assessmentPdf: 'read',
};

/** Whether a request to this endpoint sets an approved setup back. */
export const revokesApproval = (endpoint: Endpoint): boolean =>
  (Object.keys(API) as (keyof typeof API)[]).some(k => API[k] === endpoint && APPROVAL_EFFECT[k] === 'revokes');

/** The endpoint's URL: `:id` filled in (encoded), and a query when given. */
export function urlOf(endpoint: Endpoint, id?: string, query?: Record<string, string>): string {
  let url = endpoint.path;
  if (url.includes(':id')) {
    if (!id) throw new Error(`${endpoint.path} needs an id`);
    url = url.replace(':id', encodeURIComponent(id));
  }
  const search = query ? new URLSearchParams(query).toString() : '';
  return search ? `${url}?${search}` : url;
}

export type NumberFormat = 'integer' | 'decimal1' | 'eur' | 'eurCompact' | 'percent';

/** A step as the server reports it: a word, or (allowed for) an object with a reason. */
export type RawStepState = 'done' | 'todo' | 'blocked' | { state?: string; status?: string; reason?: string };

export interface SetupInput {
  key: string;
  label: string;
  cell?: string;
  example: number;
  format?: NumberFormat;
  unit?: string;
  source: { kind: 'research'; recipe: 'teachers' | 'students' } | { kind: 'assume' };
  /** The assumption's value; null only on its way to the server, to remove it (an input switched to research). */
  default?: number | null;
  plausible?: { min: number; max: number };
}

export interface SetupOutput {
  key: string;
  label: string;
  cell?: string;
  format?: NumberFormat;
  unit?: string;
  keep: boolean;
}

export interface ScenarioResult {
  inputs: Record<string, number>;
  excel: Record<string, number | string>;
  script: Record<string, number | string>;
  equal: boolean;
  differences?: string[];
}

export interface ScriptVerification {
  passed: boolean;
  scenarios: ScenarioResult[];
  reasons: string[];
  checkedAt?: string;
}

export interface TemplatePlaceholder {
  name: string;
  align?: 'left' | 'center' | 'right';
  page?: number;
  fillable: boolean;
  reason?: string;
}

export interface SetupView {
  _id: string;
  name?: string;
  createdAt?: string;
  updatedAt?: string;
  calculator?: { source: 'upload' | 'link'; url?: string; fileName: string; sheets?: string[]; warnings?: string[] };
  inputs?: SetupInput[];
  outputs?: SetupOutput[];
  script?: { source: string; attempts?: number; verification?: ScriptVerification };
  conversion?: { status: 'running' | 'passed' | 'failed'; reason?: string; feedback?: string };
  template?: {
    source: 'upload' | 'link';
    url?: string;
    fileName: string;
    inspection?: { pages?: number; placeholders?: TemplatePlaceholder[]; formFields?: string[]; warnings?: string[] };
  };
  mapping?: Record<string, string>;
  hook?: { templates: string[]; maxLength?: number };
  example?: { inputs?: Record<string, number>; outputs?: Record<string, number>; hook?: string };
  approval?: { status?: 'draft' | 'pending' | 'approved' | 'changes-requested'; feedback?: string };
  steps?: Partial<Record<'calculator' | 'fields' | 'check' | 'template' | 'example' | 'approved', RawStepState>>;
}

export type AssessmentStatus = 'researching' | 'calculated' | 'rendered' | 'checked' | 'blocked';

export type TaggedValue =
  | { key: string; label: string; value: number; kind: 'found'; source: { url: string; quote: string } }
  | { key: string; label: string; value: number; kind: 'estimated'; method?: string; basis: string };

/** A key number as the hand-over carries it: a raw number (v1) or, additively, an object (v2). */
export type RawKeyNumber = number | { key?: string; label?: string; text?: string; value?: number } | null | undefined;

export interface AssessmentView {
  id: string;
  campaignId?: string;
  leadId: string;
  setupId?: string;
  company?: string;
  website?: string;
  status: AssessmentStatus;
  blockedReasons?: string[];
  inputs?: TaggedValue[];
  tool?: { outputs?: Record<string, { ok?: boolean; value?: number; label?: string }> };
  hook?: string;
  pdf?: unknown;
  handover?: {
    /** Raw numbers per output key. */
    keyNumbers?: Record<string, RawKeyNumber> | RawKeyNumber[];
    /** The same with label and the text the PDF prints, German, in setup order (v2). */
    keyNumberDetails?: { key: string; label: string; value: number; text: string }[];
    hook?: string;
    /** German sentences for message writing: „Gelesen: … Angenommen: …“. */
    messageNotes?: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** One request: JSON in and out, the server's error as an ApiError. */
export async function apiCall<T = Record<string, unknown>>(
  url: string,
  init: { method?: string; body?: unknown } = {},
): Promise<T> {
  const resp = await fetch(url, {
    method: init.method ?? 'GET',
    ...(init.body !== undefined
      ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(init.body) }
      : {}),
  });
  let data: Record<string, unknown> = {};
  try {
    data = (await resp.json()) as Record<string, unknown>;
  } catch {
    // Not JSON (a proxy's error page): the status says enough.
  }
  if (!resp.ok || data.ok === false) {
    const error = typeof data.error === 'string' && data.error ? data.error : `Request failed (${resp.status})`;
    throw new ApiError(error, resp.status, data);
  }
  return data as T;
}

/** One request to an endpoint of the table, with its method. */
export function callApi<T = Record<string, unknown>>(
  endpoint: Endpoint,
  options: { id?: string; query?: Record<string, string>; body?: unknown } = {},
): Promise<T> {
  return apiCall<T>(urlOf(endpoint, options.id, options.query), {
    method: endpoint.method,
    ...(options.body !== undefined ? { body: options.body } : {}),
  });
}

/** The message of whatever was thrown, for a toast. */
export const messageOf = (err: unknown): string =>
  err instanceof Error && err.message ? err.message : 'Something went wrong. Please try again.';
